import { useState } from "react";
import { useAuth } from "@/App";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Shield,
  LayoutDashboard,
  Building2,
  Users,
  BookOpen,
  CreditCard,
  Megaphone,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  ClipboardList,
  Calendar,
  Award,
  FileText,
  MessageSquare,
  UsersRound,
  BarChart3,
  Bell,
  Key,
  Wand2
} from "lucide-react";
import NotificationCenter from "./NotificationCenter";

const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const getNavItems = () => {
    const baseItems = [];

    if (user?.role === 'super_admin') {
      baseItems.push(
        { path: '/admin', label: 'Dashboard', icon: LayoutDashboard },
        { path: '/companies', label: 'Companies', icon: Building2 },
        { path: '/courses', label: 'Courses', icon: BookOpen },
        { path: '/toolbox-talks', label: 'Toolbox Talks', icon: MessageSquare },
        { path: '/user-groups', label: 'User Groups', icon: UsersRound },
        { path: '/auto-assign', label: 'Auto-Assign Rules', icon: Wand2 },
        { path: '/forms', label: 'Forms', icon: ClipboardList },
        { path: '/forms/submissions', label: 'Submissions', icon: FileText },
        { path: '/calendar', label: 'Calendar', icon: Calendar },
        { path: '/certificates', label: 'Certificates', icon: Award },
        { path: '/analytics', label: 'Analytics', icon: BarChart3 },
        { path: '/billing', label: 'Billing', icon: CreditCard },
        { path: '/marketing', label: 'AI Marketing', icon: Megaphone },
        { path: '/sso', label: 'SSO Config', icon: Key }
      );
    } else if (user?.role === 'company_admin') {
      baseItems.push(
        { path: '/company', label: 'Dashboard', icon: LayoutDashboard },
        { path: '/courses', label: 'Courses', icon: BookOpen },
        { path: '/toolbox-talks', label: 'Toolbox Talks', icon: MessageSquare },
        { path: '/user-groups', label: 'User Groups', icon: UsersRound },
        { path: '/auto-assign', label: 'Auto-Assign Rules', icon: Wand2 },
        { path: '/forms', label: 'Forms', icon: ClipboardList },
        { path: '/forms/submissions', label: 'Submissions', icon: FileText },
        { path: '/calendar', label: 'Calendar', icon: Calendar },
        { path: '/certificates', label: 'Certificates', icon: Award },
        { path: '/analytics', label: 'Analytics', icon: BarChart3 }
      );
    } else {
      baseItems.push(
        { path: '/employee', label: 'My Training', icon: LayoutDashboard },
        { path: '/courses', label: 'Browse Courses', icon: BookOpen },
        { path: '/toolbox-talks', label: 'Toolbox Talks', icon: MessageSquare },
        { path: '/forms', label: 'Forms', icon: ClipboardList },
        { path: '/certificates', label: 'My Certificates', icon: Award },
        { path: '/settings/notifications', label: 'Notifications', icon: Bell }
      );
    }

    return baseItems;
  };

  const navItems = getNavItems();

  const getRoleLabel = () => {
    switch (user?.role) {
      case 'super_admin': return 'Administrator';
      case 'company_admin': return 'Company Admin';
      default: return 'Employee';
    }
  };

  return (
    <div className="min-h-screen bg-secondary flex">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex flex-col w-64 bg-primary text-white fixed h-full">
        <div className="p-6 border-b border-slate-700">
          <Link to="/dashboard" className="flex items-center gap-2">
            <Shield className="w-8 h-8 text-accent" />
            <span className="font-heading font-bold text-lg">SafetyTrack Pro</span>
          </Link>
        </div>

        <ScrollArea className="flex-1 py-4">
          <nav className="px-3 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  className={`sidebar-nav-item ${isActive ? 'active' : ''}`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>
        </ScrollArea>

        <div className="p-4 border-t border-slate-700">
          {/* Notification Bell */}
          <div className="flex items-center justify-end mb-3">
            <NotificationCenter />
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-3 w-full p-2 rounded-lg hover:bg-slate-800 transition-colors" data-testid="user-menu-btn">
                <Avatar className="w-9 h-9">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-accent text-white">
                    {user?.name?.charAt(0) || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium truncate">{user?.name}</p>
                  <p className="text-xs text-slate-400">{getRoleLabel()}</p>
                </div>
                <ChevronDown className="w-4 h-4 text-slate-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem disabled className="text-muted-foreground">
                {user?.email}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <Link to="/settings/notifications">
                <DropdownMenuItem data-testid="notification-settings-link">
                  <Bell className="w-4 h-4 mr-2" />
                  Notification Settings
                </DropdownMenuItem>
              </Link>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} data-testid="logout-btn" className="text-destructive">
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-primary text-white">
        <div className="flex items-center justify-between p-4">
          <Link to="/dashboard" className="flex items-center gap-2">
            <Shield className="w-7 h-7 text-accent" />
            <span className="font-heading font-bold">SafetyTrack Pro</span>
          </Link>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-white hover:bg-slate-800"
            data-testid="mobile-menu-btn"
          >
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/50" onClick={() => setSidebarOpen(false)}>
          <aside className="w-64 bg-primary text-white h-full" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-slate-700 mt-14">
              <p className="text-sm text-slate-400">Welcome,</p>
              <p className="font-medium">{user?.name}</p>
            </div>

            <nav className="p-3 space-y-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={`sidebar-nav-item ${isActive ? 'active' : ''}`}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </nav>

            <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-700">
              <Button 
                variant="ghost" 
                onClick={logout}
                className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-800"
                data-testid="mobile-logout-btn"
              >
                <LogOut className="w-5 h-5 mr-3" />
                Sign Out
              </Button>
            </div>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 lg:ml-64 min-h-screen">
        <div className="pt-16 lg:pt-0">
          {children}
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;
